import React, { useState } from 'react';
import { AlertTriangle, FileText, Info, ChevronDown, ChevronUp } from 'lucide-react';

const LV2Diagnostic = ({ lv2Data }) => {
  // Replié par défaut : sur mobile, le diagnostic complet créait un mur de texte
  // qui repoussait le CTA hors de portée. Le résumé reste visible, le détail se déplie.
  const [expanded, setExpanded] = useState(false);

  if (!lv2Data || Object.keys(lv2Data).length === 0) {
    return null;
  }

  const {
    resume_pedagogique_du_dossier,
    points_de_vigilance,
    zones_d_incertaines_majeures,
    pieces_manquantes_necessaires,
    avertissement_utilisateur
  } = lv2Data;

  // S'assurer que les tableaux sont bien des tableaux (protection contre anciennes versions du backend)
  const safePointsVigilance = Array.isArray(points_de_vigilance) ? points_de_vigilance : [];
  const safeZonesIncertaines = Array.isArray(zones_d_incertaines_majeures) ? zones_d_incertaines_majeures : [];
  const safePiecesManquantes = Array.isArray(pieces_manquantes_necessaires) ? pieces_manquantes_necessaires : [];

  return (
    <div className="card p-8 mb-8">
      <h3 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
        📋 Diagnostic de votre dossier
      </h3>

      {/* Résumé pédagogique */}
      {resume_pedagogique_du_dossier && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-lg font-semibold mb-3 flex items-center" style={{ color: '#1e3a8a' }}>
            <Info size={20} className="mr-2" />
            Résumé de votre situation
          </h4>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {resume_pedagogique_du_dossier}
          </p>
        </div>
      )}

      {/* Toggle du détail (points de vigilance, incertitudes, pièces manquantes) */}
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors"
          style={{ color: '#1e3a8a' }}
        >
          Voir le détail du diagnostic
          {safePointsVigilance.length + safeZonesIncertaines.length + safePiecesManquantes.length > 0 &&
            ` (${safePointsVigilance.length + safeZonesIncertaines.length + safePiecesManquantes.length} points)`}
          <ChevronDown size={16} />
        </button>
      )}

      {expanded && (
      <>
      {/* Points de vigilance */}
      {safePointsVigilance.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h4 className="text-lg font-semibold mb-3 flex items-center text-yellow-800">
            <AlertTriangle size={20} className="mr-2" />
            À surveiller dans votre dossier
          </h4>
          <ul className="space-y-2">
            {safePointsVigilance.map((point, index) => (
              <li key={index} className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <span className="text-gray-700">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Zones d'incertitude */}
      {safeZonesIncertaines.length > 0 && (
        <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <h4 className="text-lg font-semibold mb-3 flex items-center text-orange-800">
            <AlertTriangle size={20} className="mr-2" />
            Ce qui reste flou pour l'instant
          </h4>
          <ul className="space-y-2">
            {safeZonesIncertaines.map((zone, index) => (
              <li key={index} className="flex items-start">
                <span className="text-orange-600 mr-2">⚠️</span>
                <span className="text-gray-700">{zone}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pièces manquantes */}
      {safePiecesManquantes.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
          <h4 className="text-lg font-semibold mb-3 flex items-center text-red-800">
            <FileText size={20} className="mr-2" />
            Documents à retrouver pour renforcer votre dossier
          </h4>
          <ul className="space-y-2">
            {safePiecesManquantes.map((piece, index) => (
              <li key={index} className="flex items-start">
                <span className="text-red-600 mr-2">📄</span>
                <span className="text-gray-700">{piece}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Avertissement */}
      {avertissement_utilisateur && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-300">
          <p className="text-sm text-gray-600 italic">
            ⚖️ {avertissement_utilisateur}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded(false)}
        className="mt-4 w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-gray-700"
      >
        Replier le détail <ChevronUp size={16} />
      </button>
      </>
      )}
    </div>
  );
};

export default LV2Diagnostic;
